import React, { Dispatch, SetStateAction } from "react";
import {
  ShareSection,
  ShareSuccessContext,
} from "components/shareSection/ShareSection";
import {
  ShareOutcome,
  Permission,
  RefetchOptions,
  RemoveAccessData,
  ResourceType,
} from "components/shareSection/entities";
import { UserModelRole, SharedAccess } from "target_wasm";
import { Dialog } from "components/main/utils/Dialog";
import { SpaceAccess } from "components/shareSection/useModelSpaceAccess";
import { LoadFailed, LoadingRows } from "components/shareSection/AccessItem";

export interface ManageAccessDialogProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  resourceType: ResourceType;
  sharedWithList: SharedAccess | undefined;
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>;
  canShare: boolean;
  canRevokeAccess: boolean;
  onShare: (
    emails: string[],
    roleOrPermission: UserModelRole | Permission | null,
  ) => Promise<ShareOutcome | void>;
  onRevokeAccess: (data: RemoveAccessData | null) => Promise<void>;
  onSuccess?: (context: ShareSuccessContext) => void;
  onError?: (error: string, status?: number) => void;
  defaultRole?: UserModelRole;
  defaultPermission?: Permission;
  onRefetchSharedList?: (options?: RefetchOptions) => Promise<boolean | void>;
  userContinuationToken?: string | null;
  onResendInvitation?: (email: string) => Promise<void>;
  /// Above the composer: what sharing gives people.
  notice?: React.ReactNode;
  /// Under the composer: a dataset's co-grant toggle.
  shareOptions?: React.ReactNode;
  /// Shows the composer but blocks Share (e.g. awaiting a published release);
  /// `canShare` decides whether the composer shows at all.
  shareDisabled?: boolean;
  /// The model's space group, for someone who can see the space.
  space?: SpaceAccess;
  /// The reader's own role on the model, for when nobody else is listed.
  viewerRole?: string;
  /// The first load failed: a way to retry instead of the loading state.
  loadFailed?: boolean;
  onRetryLoad?: () => void;
}

// A main page centres it on the pane: the window would count the sidebar.
const MAIN_PANE: React.RefObject<HTMLElement | null> = {
  get current() {
    return document.querySelector<HTMLElement>("[data-main-pane]");
  },
};

export const ManageAccessDialog: React.FC<ManageAccessDialogProps> = (
  props,
) => {
  return (
    <Dialog
      open={props.open}
      title="Manage Access"
      onClose={props.onClose}
      // Unanchored where there is no pane (the canvas): an anchor that never
      // measures would leave the dialog invisible.
      anchorRef={MAIN_PANE.current ? MAIN_PANE : undefined}
      // 640: the composer fits two long addresses per row beside the role and
      // "Share"; none of the Dialog sizes does.
      className="w-160! max-w-[90vw] overflow-visible"
      autoSpacing={false}
      overlayScrollbar
    >
      {props.sharedWithList === undefined ? (
        props.loadFailed ? (
          <LoadFailed onRetry={props.onRetryLoad}>
            Couldn’t load the people with access.
          </LoadFailed>
        ) : (
          <LoadingRows label="Loading the people with access" />
        )
      ) : (
        <div className="flex flex-col w-full overflow-visible">
          <ShareSection
            sharedWithList={props.sharedWithList}
            setSharedWithList={props.setSharedWithList}
            entityId={props.entityId}
            resourceType={props.resourceType}
            canShare={props.canShare}
            shareDisabled={props.shareDisabled}
            canRevokeAccess={props.canRevokeAccess}
            onShare={props.onShare}
            onRevokeAccess={props.onRevokeAccess}
            onSuccess={props.onSuccess}
            onError={props.onError}
            defaultRole={props.defaultRole}
            defaultPermission={props.defaultPermission}
            onRefetchSharedList={props.onRefetchSharedList}
            userContinuationToken={props.userContinuationToken}
            onResendInvitation={props.onResendInvitation}
            space={props.space}
            viewerRole={props.viewerRole}
            notice={props.notice}
            shareOptions={props.shareOptions}
          />
        </div>
      )}
    </Dialog>
  );
};
